import { useNavigate } from "react-router-dom";
import { X, Plus, Minus, ShoppingBag } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";
import { imgUrl, money } from "@/lib/api";

export const CartDrawer = () => {
  const { items, open, setOpen, updateQty, removeItem, subtotal, count } = useCart();
  const navigate = useNavigate();

  const goCheckout = () => {
    setOpen(false);
    navigate("/cart");
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent className="flex w-full flex-col p-0 sm:max-w-md" data-testid="cart-drawer">
        <SheetHeader className="border-b px-6 py-5">
          <SheetTitle className="font-display flex items-center gap-2 text-lg font-normal">
            <ShoppingBag size={18} /> Your Cart ({count})
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 px-6 text-center">
            <p className="text-zinc-500">Your cart is empty.</p>
            <Button onClick={() => setOpen(false)} variant="outline" className="rounded-none">
              Continue Shopping
            </Button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {items.map((i) => (
                <div key={i.id} className="flex gap-4 border-b py-4" data-testid={`cart-line-${i.id}`}>
                  <div className="h-24 w-20 shrink-0 bg-zinc-100">
                    {i.image && <img src={imgUrl(i.image)} alt={i.title} className="h-full w-full object-cover" />}
                  </div>
                  <div className="flex flex-1 flex-col">
                    <div className="flex justify-between gap-2">
                      <p className="text-sm font-medium leading-snug">{i.title}</p>
                      <button onClick={() => removeItem(i.id)} data-testid={`cart-remove-${i.id}`}>
                        <X size={16} className="text-zinc-400 hover:text-zinc-900" />
                      </button>
                    </div>
                    <p className="mt-1 text-sm text-zinc-500">{money(i.price)}</p>
                    <div className="mt-auto flex items-center border border-zinc-300">
                      <button className="px-2 py-1.5" onClick={() => updateQty(i.id, i.quantity - 1)}>
                        <Minus size={13} />
                      </button>
                      <span className="min-w-8 text-center text-sm" data-testid={`cart-qty-${i.id}`}>{i.quantity}</span>
                      <button className="px-2 py-1.5" onClick={() => updateQty(i.id, i.quantity + 1)}>
                        <Plus size={13} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="border-t px-6 py-5">
              <div className="mb-4 flex items-center justify-between">
                <span className="text-sm uppercase tracking-wider text-zinc-500">Subtotal</span>
                <span className="font-display text-xl" data-testid="cart-subtotal">{money(subtotal)}</span>
              </div>
              <Button
                onClick={goCheckout}
                className="h-12 w-full rounded-none bg-zinc-900 text-xs uppercase tracking-widest hover:bg-zinc-800"
                data-testid="cart-checkout-btn"
              >
                Checkout
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
